import React, { useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "@store/store";
import { showNotification } from "@libs/notistack/notificationStack";
import type { NotificationStreamProviderProps } from "./types/types";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import { addNotification } from "@store/slices/notificationSlice";
import { SERVER_URL } from "@config/env";
import { SERVER_ROUTES } from "@constants/api.routes";
import { useTranslation } from "react-i18next";

const RECONNECT_INTERVAL = 30000; // 30 seconds

const NotificationStreamProvider: React.FC<NotificationStreamProviderProps> = ({ children }) => {
	const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
	const user = useSelector((state: RootState) => state.user.user);
	const dispatch = useDispatch<AppDispatch>();
	const eventSourceRef = useRef<EventSource | null>(null);
	const reconnectTimeout = useRef<number | null>(null);
	const { t } = useTranslation();

	// Transform notification data to match expected structure
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const transformNotificationData = (notification: any) => ({
		...notification,
		forUser:
			typeof notification.forUser === "string"
				? {
						id: parseInt(notification.forUser),
						uuid: "",
						firstName: null,
						lastName: null,
						emailAddress: "",
						phoneNumber: "",
						cnic: null,
						landlineNumber: null,
						profilePicture: null,
						userTypeId: 0,
				  }
				: notification.forUser,
		fromUser:
			typeof notification.fromUser === "string"
				? {
						id: parseInt(notification.fromUser),
						uuid: "",
						firstName: null,
						lastName: null,
						emailAddress: "",
						phoneNumber: "",
						cnic: null,
						landlineNumber: null,
						profilePicture: null,
						userTypeId: 0,
				  }
				: notification.fromUser,
	});

	// Handle new notification event
	const handleNewNotification = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(data: any) => {
			if (data?.type === "new" && data?.notification) {
				const notification = transformNotificationData(data.notification);

				showNotification({
					severity: SNACKBAR_SEVERITY.INFO,
					title: notification.title || t("psdaCommon.notifications.title"),
					description: notification.message || t("psdaCommon.notifications.newNotification"),
				});

				dispatch(addNotification(notification));
			}
		},
		[dispatch, t],
	);

	// Connect to notification stream
	const connect = useCallback(() => {
		if (!user?.id) return;

		const url = `${SERVER_URL}${SERVER_ROUTES.NOTIFICATIONS.STREAM(user.id)}`;
		const es = new EventSource(url);
		eventSourceRef.current = es;

		es.addEventListener("message", (event: MessageEvent) => {
			try {
				const data = JSON.parse(event.data);
				handleNewNotification(data);
			} catch {
				showNotification({
					message: t("psdaCommon.notifications.api.errors.stream.errorProcessingNotificationEvent"),
					severity: SNACKBAR_SEVERITY.ERROR,
				});
			}
		});

		es.onerror = () => {
			// showNotification({
			// 	message: t("psdaCommon.notifications.api.errors.stream.notificationConnectionLost"),
			// 	severity: SNACKBAR_SEVERITY.WARNING,
			// });
			es.close();

			if (!reconnectTimeout.current) {
				reconnectTimeout.current = window.setTimeout(() => {
					reconnectTimeout.current = null;
					connect();
				}, RECONNECT_INTERVAL);
			}
		};
		es.onopen = () => console.log("Connected to notification stream");
	}, [user?.id, handleNewNotification, t]);

	useEffect(() => {
		// Only run in browser and if EventSource is supported
		if (typeof window === "undefined" || !("EventSource" in window)) return;
		if (!isAuthenticated || !user?.id) return;

		connect();

		return () => {
			if (eventSourceRef.current) {
				eventSourceRef.current.close();
			}
			if (reconnectTimeout.current) {
				clearTimeout(reconnectTimeout.current);
			}
		};
	}, [isAuthenticated, user?.id, connect]);

	// Fallback for unsupported browsers
	if (typeof window !== "undefined" && !("EventSource" in window)) {
		showNotification({
			message: t("psdaCommon.notifications.api.errors.stream.browserNotSupported"),
			severity: SNACKBAR_SEVERITY.ERROR,
		});
		return <>{children}</>;
	}

	return <>{children}</>;
};

export default NotificationStreamProvider;
